import { Box, Button, HStack, Icon, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLock } from "react-icons/fi";
import {
  PortalEventContextType,
  PortalInviteStatusDto,
  PortalLifecycleStatus,
  useAcceptPortalInviteMutation,
  useDeclinePortalInviteMutation,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";

export const isPrivatePortalEvent = (event: PortalSourcingEventDetailDto): boolean =>
  event.contextType === PortalEventContextType.Private;

export const PrivateInviteCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  // The server-persisted invite status is the source of truth; a successful accept/decline is
  // mirrored locally for instant feedback while the detail query refetches.
  const [localStatus, setLocalStatus] = useState<PortalInviteStatusDto | null>(null);
  const [accept, { isLoading: isAccepting }] = useAcceptPortalInviteMutation();
  const [decline, { isLoading: isDeclining }] = useDeclinePortalInviteMutation();

  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const status = localStatus ?? event.myInviteStatus ?? PortalInviteStatusDto.NotInvited;

  if (!isPrivatePortalEvent(event) || !isOpen) return null;

  const handleAccept = async () => {
    try {
      await accept({ eventId: event.id }).unwrap();
      setLocalStatus(PortalInviteStatusDto.Accepted);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  const handleDecline = async () => {
    try {
      await decline({ eventId: event.id }).unwrap();
      setLocalStatus(PortalInviteStatusDto.Declined);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
      <HStack spacing="4" justify="space-between" align="center" flexWrap="wrap" rowGap="3">
        <HStack spacing="3" align="center">
          <Icon as={FiLock} boxSize="5" color="smPrimary" flexShrink="0" />
          <Stack spacing="0">
            <Text fontWeight="semibold">{t("You have been invited to this procurement")}</Text>
            <Text fontSize="sm" color="smMuted">
              {t("Accept or decline your invitation to participate.")}
            </Text>
          </Stack>
        </HStack>

        {isAccepting || isDeclining ? (
          <Spinner size="sm" color="smPrimary" flexShrink="0" />
        ) : status === PortalInviteStatusDto.Accepted ? (
          <Text fontWeight="semibold" color="smSuccess" flexShrink="0">
            {t("Invitation accepted!")}
          </Text>
        ) : status === PortalInviteStatusDto.Declined ? (
          <Text fontWeight="semibold" color="smMuted" flexShrink="0">
            {t("Invitation declined")}
          </Text>
        ) : (
          <HStack spacing="3" flexShrink="0">
            <Button variant="outline" colorScheme="gray" onClick={handleDecline} isLoading={isDeclining}>
              {t("Decline")}
            </Button>
            <Button bg="smPrimary" color="white" _hover={{ opacity: 0.9 }} onClick={handleAccept} isLoading={isAccepting}>
              {t("Accept invitation")}
            </Button>
          </HStack>
        )}
      </HStack>
    </Box>
  );
};
